import { Component, inject, signal, WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { marker } from '@eventlyte/common';
import {
	CheckboxField,
	CustomFormControl,
	DropdownField,
	EmailField,
	TagField,
	TagListField,
	TextField
} from '@eventlyte/forms';
import {
	ApiErrorList,
	COUNTRY_SERVICE,
	FormErrorsModal,
	RegistrationCountryField,
	RegistrationPhoneNumberField,
	RegistrationStepperControls,
	RegistrationStepperProgress,
	ServerErrorsModal
} from '@eventlyte/registration';
import { TranslocoDirective } from '@jsverse/transloco';
import { AppRoutePaths } from '../../app.routes';
import { LocalRegistrationPageBase } from '../../shared/local-registration-page-base';

@Component({
	selector: 'app-registration-information-page',
	imports: [
		RegistrationStepperControls,
		RegistrationStepperProgress,
		ServerErrorsModal,
		FormErrorsModal,
		RegistrationCountryField,
		RegistrationPhoneNumberField,
		TextField,
		EmailField,
		TranslocoDirective,
		DropdownField,
		TagField,
		CheckboxField,
		TagListField
	],
	templateUrl: './registration-information-page.html',
	styleUrls: ['./registration-information-page.scss']
})
export class RegistrationInformationPage extends LocalRegistrationPageBase {
	protected formGroup: FormGroup = new FormGroup({
		salutation: new CustomFormControl('fields.salutation.label', [this.validators.required()]),
		firstName: new CustomFormControl('fields.firstName.label', [
			this.validators.required(),
			this.validators.unicodeLettersOnly()
		]),
		lastName: new CustomFormControl('fields.lastName.label', [
			this.validators.required(),
			this.validators.unicodeLettersOnly()
		]),
		companyName: new CustomFormControl('fields.companyName.label', [
			this.validators.required()
		]),
		jobTitle: new CustomFormControl('fields.jobTitle.label', [this.validators.required()]),
		emailAddress: new CustomFormControl('fields.emailAddress.label', [
			this.validators.required(),
			this.validators.emailValidator()
		]),
		confirmEmailAddress: new CustomFormControl('content.fields.confirmEmailAddress.label', [
			this.validators.shouldEqualControl(
				'emailAddress',
				marker('content.validation.emailMatch.summary'),
				marker('content.validation.emailMatch.summary')
			),
			this.validators.required(),
			this.validators.emailValidator()
		]),
		placeOfResidence: new CustomFormControl('fields.placeOfResidence.label', [
			this.validators.required()
		]),
		mobileNumber: new CustomFormControl('fields.mobileNumber.label', [
			this.validators.minimumLength(8),
			this.validators.phoneNumber(),
			this.validators.required()
		]),
		bgovPrivacy1: new CustomFormControl('fields.bgovPrivacy1.label', [
			this.validators.required()
		]),
		bgovPrivacy3: new CustomFormControl('fields.bgovPrivacy3.label', [
			this.validators.required()
		]),
		bgovAcceptDataSharingNotice: new CustomFormControl(
			'fields.bgovAcceptDataSharingNotice.label',
			[this.validators.required()]
		),
		languageForCommunications: new CustomFormControl('fields.languageForCommunications.label', [
			this.validators.required()
		])
	});
	protected override record = signal<Record<string, any>>({ mobileNumber: '+852' });

	private countryService = inject(COUNTRY_SERVICE);

	public override ngOnInit(): void {
		super.ngOnInit();

		this.formGroups = [this.formGroup];
	}

	protected continue(): void {
		super.goToNextStep(this.formGroups, () => {
			this.saving.set(true);
			this.setDefaultValuesForRecord(this.record);
			this.extraRecords().forEach((extraRecord) =>
				this.setDefaultValuesForRecord(extraRecord)
			);

			const updateRecordWithResponseValues = (
				record: WritableSignal<Record<string, any>>,
				responseRecord: Record<string, any>
			) => {
				this.setRecordValue(record, 'id', responseRecord['id']);
				this.setRecordValue(record, 'cardNumber', responseRecord['cardNumber']);
				this.setRecordValue(
					record,
					'registrationStatus',
					responseRecord['registrationStatus']
				);
			};

			this.registrationService
				.register(this.recordStateService.getRecord(), false, this.currentStepNumber)
				.subscribe({
					next: (record) => {
						updateRecordWithResponseValues(this.record, record.record);

						Object.entries(record.record).forEach(([key, value]) =>
							this.setRecordValue(this.record, key, value)
						);

						if (record.record['onlineRegistrationCompleted']) {
							this.router.navigate([AppRoutePaths.RegistrationConfirmation]);
							return;
						}

						record.extraRecords.forEach((extraRecord, index) => {
							updateRecordWithResponseValues(this.extraRecords()[index], extraRecord);
						});

						this.router.navigate([AppRoutePaths.RegistrationQuestions]);
					},
					error: (apiErrorList: ApiErrorList) => {
						this.serverErrorList.set(apiErrorList);
						this.showServerErrors.set(true);
						this.saving.set(false);
					}
				});
		});
	}

	private setDefaultValuesForRecord(record: WritableSignal<Record<string, any>>): void {
		this.setRecordValue(
			record,
			'registrationLanguageCode',
			this.translocoService.getActiveLang()
		);

		if (sessionStorage.getItem('origin')) {
			this.setRecordValue(record, 'origin', sessionStorage.getItem('origin'));
		}
	}

	protected override setRecordValue(
		record: WritableSignal<Record<string, any>>,
		key: string,
		value: any | undefined
	): void {
		super.setRecordValue(record, key, value);

		if (key === 'placeOfResidence') {
			this.countryService.getCountries().subscribe((countries) => {
				const country = countries.find((c) => c.code === value);

				if (
					country &&
					!this.getRecordValue(record, 'mobileNumber').startsWith(
						'+' + country.dialingCode
					)
				) {
					this.setRecordValue(record, 'mobileNumber', '+' + country.dialingCode);
				}
			});
		}
	}
}
